/* eslint-disable indent */
import { IReleaseDate } from '@/types/IRelease'
import { getArtworksFromArtist } from 'api/artworks'
import { Release, getRelease, saveRelease, unsaveRelease } from 'api/releases'
import classNames from 'classnames'
import { ArtworkCard } from 'components/artwork/ArtworkCard'
import { ArtworkDetails } from 'components/artwork/ArtworksDetails'
import { Carousel } from 'components/carousels/Carousel'
import { Button } from 'components/common/Button'
import { CurrencyDisplay } from 'components/common/CurrencyDisplay'
import { Icon } from 'components/common/Icon'
import { Separator } from 'components/common/Separator'
import { Size, SizeDisplay } from 'components/common/SizeDisplay'
import { SlideShow } from 'components/common/SlideShow'
import { ToastErrorIcon } from 'components/common/Toast'
import {
  ReleaseDate,
  ReleaseDetails,
} from 'components/release-calendar/ReleaseDetails'
import { ReleaseInfoTag } from 'components/release-calendar/ReleaseInfoTag'
import { SavedReleaseModal } from 'components/release-calendar/SavedReleaseModal'
import routes from 'constants/routes'
import {
  useAuth,
  useAuthModal,
  useLoading,
  useOverlay,
  useSavedReleasesIds,
} from 'contexts'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks'
import { GetServerSideProps } from 'next'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import qs from 'qs'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { flatCollectionType, mapIArtworksToArtworkCards } from 'utils/data'
import { getImageUrl } from 'utils/image'
import { getExactReleaseDate } from 'utils/release'
import { getArtistUrl } from 'utils/urls'

type ReleasePageProps = {
  release: Release
}

export const ReleasePage = ({ release }: ReleasePageProps) => {
  const { t } = useTranslation('release_calendar')
  const router = useRouter()
  const { isAuthenticated } = useAuth()
  const { setIsOpen: setIsAuthModalOpen, setState: setAuthModalState } =
    useAuthModal()
  const { setConfig, setIsOpen } = useOverlay()
  const { data: savedIds } = useSavedReleasesIds()
  const {
    state: { isReleasePage },
  } = useLoading()
  const { data: userProfile } = useUserProfile()
  const queryClient = useQueryClient()

  const artist = release?.artists?.data?.[0]
  const artistImage = artist?.attributes?.images?.data?.[0]
  const flatArtistImage = artistImage
    ? flatCollectionType(artistImage)
    : undefined

  const artworkImage =
    release.artworkImages?.data?.find(image =>
      image.attributes.name?.includes('(2)')
    ) || release.artworkImages?.data?.[0]
  const flatArtworkImage = artworkImage
    ? flatCollectionType(artworkImage)
    : undefined

  const isSaved = savedIds?.includes(release.id)

  const { releaseStart: exactReleaseDate, releaseEnd: exactReleaseEndDate } =
    getExactReleaseDate(
      release.releaseDate as IReleaseDate,
      userProfile?.releaseSettings?.timeZone || dayjs.tz.guess(),
      release.raffle
    )

  const canSaveRelease =
    !release.releaseDate?.dateOnly &&
    dayjs().isBefore(dayjs(exactReleaseDate), 'milliseconds')

  const { data: moreArtworks } = useQuery(
    ['more-artworks', artist?.id],
    async () => {
      if (!artist?.id) return []

      const { data } = await getArtworksFromArtist(null, artist.id)

      if (!data.length) return []

      return mapIArtworksToArtworkCards(data)
    },
    { refetchOnWindowFocus: false }
  )

  const refetchSavedReleases = () => {
    queryClient.refetchQueries({ queryKey: ['releases', 'saved'] })
  }

  const onNotifyMe = async () => {
    if (!isAuthenticated) {
      setAuthModalState('login')
      setIsAuthModalOpen(true)
      return
    }

    const { error } = await saveRelease(release.id)

    if (error) {
      toast.error(error.message, { icon: <ToastErrorIcon /> })
      return
    }

    const image = release.artworkImages?.data?.[0]

    setConfig({
      className: 'flex items-center px-4',
      body: (
        <SavedReleaseModal
          imageAlt={release.name || ''}
          imageSrc={
            image
              ? getImageUrl(flatCollectionType(image), 'thumbnail')
              : undefined
          }
        />
      ),
      preventScrollDisable: true,
    })
    setIsOpen(true)
    refetchSavedReleases()
  }

  const onUnsave = async () => {
    const { error } = await unsaveRelease(release.id)

    if (error) {
      toast.error(error.message, { icon: <ToastErrorIcon /> })
      return
    }

    refetchSavedReleases()
  }

  const closeDialog = () => {
    router.push(
      {
        pathname: routes.releaseCalendar.base,
        query: { tab: router.query.tab },
      },
      undefined,
      {
        scroll: false,
      }
    )
  }

  return (
    <>
      <div>
        <button
          type='button'
          className='pointer-events-auto fixed right-3 top-[4.25rem] z-[101] rounded-full bg-cool-50 p-4 shadow-popup sm:hidden'
          onClick={closeDialog}
        >
          <span className='sr-only'>{t('common:close')}</span>
          <Icon name='close' size={16} />
        </button>

        <div
          className={classNames(
            'fixed left-0 top-0 z-[100] h-full w-full',
            !isReleasePage && 'bg-cool-900/60'
          )}
          onClick={closeDialog}
          data-release-overlay
        />

        <div
          className={classNames(
            'fixed left-0 top-0 z-[100] flex h-full w-full flex-col items-center justify-center pt-14 sm:left-1/2 sm:max-w-[37.5rem] sm:-translate-x-1/2 sm:py-10 [@media(max-width:639px)]:!opacity-100'
            // classes['release-dialog']
          )}
        >
          <div className='h-full w-full overflow-y-auto rounded-t-xl bg-cool-50 sm:h-max sm:rounded-xl'>
            <div className='relative flex h-full flex-col overflow-x-hidden'>
              <div className='hidden w-full items-center justify-between bg-cool-50 px-6 pb-4 pt-6 sm:sticky sm:top-0 sm:z-[2] sm:flex'>
                <h2 className='font-body text-2xl font-normal tracking-normal'>
                  {release.name}
                </h2>

                <button type='button' onClick={closeDialog}>
                  <span className='sr-only'>{t('common:close')}</span>
                  <Icon name='close' size={24} className='text-cool-600' />
                </button>
              </div>

              <div className='relative px-12 pt-5 sm:px-6 sm:pt-0 [&_div]:data-[mobile-image-container=true]:rounded-lg'>
                <SlideShow
                  images={flatArtworkImage ? [flatArtworkImage] : undefined}
                  mobileOnly
                  imageContainerClassName='rounded-lg sm:rounded-none'
                />

                <ReleaseInfoTag
                  className='absolute left-9 top-8 z-[1] shadow-popup sm:left-6 sm:top-4'
                  releaseDate={release.releaseDate}
                  raffle={release.raffle}
                />
              </div>

              <div className='h-full w-full pt-6 sm:pt-4'>
                <div className='px-6 pb-10 sm:pb-8'>
                  <h2 className='mb-5 font-body text-xl font-medium tracking-normal sm:hidden'>
                    {release.name}
                  </h2>

                  <div className='flex flex-col space-y-4 sm:grid sm:grid-cols-2 sm:gap-x-4 sm:gap-y-6 sm:space-y-0'>
                    {artist && (
                      <ReleaseDetails
                        title={t('artist')}
                        subtitle={
                          <>
                            {artist.attributes.name && (
                              <Link
                                href={getArtistUrl(
                                  artist.attributes.name,
                                  artist.id
                                )}
                                className='transition-colors hover:text-secondary-default'
                              >
                                {artist.attributes.name}
                              </Link>
                            )}

                            {release.extraArtists && (
                              <span>
                                {artist.attributes.name && ' x '}
                                {release.extraArtists}
                              </span>
                            )}
                          </>
                        }
                        image={
                          <Image
                            src={getImageUrl(flatArtistImage, 'smallest')}
                            alt={artist.attributes.name || ''}
                            width={40}
                            height={40}
                            className='h-10 w-10 rounded-[10px] object-cover'
                            sizes='10vw'
                          />
                        }
                      />
                    )}

                    {!artist && release.extraArtists && (
                      <ReleaseDetails
                        title={t('artist')}
                        subtitle={release.extraArtists}
                        image='profile'
                      />
                    )}

                    {(exactReleaseDate || exactReleaseEndDate) && (
                      <>
                        {release.releaseDate?.dateOnly && (
                          <ReleaseDate
                            exactReleaseDate={exactReleaseDate}
                            tbd
                          />
                        )}

                        {!release.releaseDate?.dateOnly && (
                          <ReleaseDate
                            exactReleaseDate={exactReleaseDate}
                            exactReleaseEndDate={exactReleaseEndDate}
                          />
                        )}
                      </>
                    )}

                    {!exactReleaseDate && !release.releaseDate?.dateOnly && (
                      <ReleaseDetails
                        title={t('release_date')}
                        subtitle={t('tbd')}
                        image='time'
                      />
                    )}

                    {Boolean(release.price) && (
                      <ReleaseDetails
                        title={t('price')}
                        subtitle={
                          <CurrencyDisplay
                            price={release.price}
                            currency={release.currency}
                          />
                        }
                        image='cart-2'
                      />
                    )}

                    {release.size &&
                      (release.size.depth ||
                        release.size.diameter ||
                        release.size.height ||
                        release.size.width) && (
                        <ReleaseDetails
                          title={t('dimensions')}
                          subtitle={
                            <SizeDisplay
                              size={release.size as Size}
                              className='!space-x-3'
                            />
                          }
                          image='full-screen-resize-expand'
                        />
                      )}
                  </div>

                  <Separator className='my-6 sm:my-5' color='cool-300' />

                  <h3 className='mb-2 font-body text-lg font-medium tracking-normal'>
                    {t('additional_info')}
                  </h3>

                  <ArtworkDetails
                    withLoader={false}
                    editionSize={release.editionSize}
                    medium={release.medium}
                    material={release.material}
                    features={release.features}
                    includes={release.includes}
                    information={release.additionalInformation}
                    gallery={release.gallery?.data?.attributes?.name}
                  />
                </div>

                {artist && moreArtworks && Boolean(moreArtworks.length) && (
                  <>
                    <div>
                      <Separator className='mb-8' color='cool-400' />
                    </div>

                    <Carousel
                      title={t('more_from_artist')}
                      viewAllLabel={t('common:view_all')}
                      viewAllHref={getArtistUrl(
                        artist?.attributes?.name || '',
                        artist.id
                      )}
                      titleClassName='text-lg font-medium font-body tracking-normal'
                      className='pb-12'
                      headerClassName='!px-6'
                      bodyClassName='!pl-6 !pr-6 [&>*]:sm:!w-[calc(36%_-_2_/_3_*_theme(margin.4))]'
                      mobileOnly
                    >
                      {moreArtworks?.map(artwork => (
                        <ArtworkCard key={artwork.id} {...artwork} />
                      ))}
                    </Carousel>
                  </>
                )}

                {(release.url || canSaveRelease) && (
                  <div className='sticky bottom-0 flex w-full flex-col-reverse bg-cool-50 px-6 py-4 shadow-[0px_-2px_8px_0px_rgba(0,_0,_0,_0.05)]  sm:flex-row sm:space-x-2 sm:space-y-0'>
                    {release.url && (
                      <Button
                        style='solid-white'
                        pill
                        className='mt-2 w-full sm:mt-0'
                        type='link'
                        href={release.url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <span>{t('link')}</span>

                        <Icon
                          name='external-link'
                          size={24}
                          className='ml-3 text-secondary-default'
                        />
                      </Button>
                    )}

                    {!isSaved && canSaveRelease && (
                      <Button
                        style='solid-primary'
                        pill
                        className='w-full'
                        onClick={onNotifyMe}
                      >
                        <span>{t('notify_me')}</span>

                        <Icon
                          name='filled-bell'
                          filled
                          size={20}
                          className='ml-3 text-cool-50'
                        />
                      </Button>
                    )}

                    {isSaved && canSaveRelease && (
                      <Button
                        style='solid-primary'
                        pill
                        className='w-full'
                        onClick={onUnsave}
                      >
                        <span>{t('saved')}</span>

                        <Icon
                          name='check'
                          size={20}
                          className='ml-3 text-cool-50'
                        />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

async function fetchRelease(id: number) {
  const query = qs.stringify(
    {
      populate: {
        artworkImages: {
          fields: '*',
        },
        artists: {
          fields: ['name'],
          populate: {
            images: {
              fields: '*',
            },
          },
        },
        artwork: {
          fields: ['name'],
        },
        features: {
          fields: '*',
        },
        includes: {
          fields: '*',
        },
        size: {
          fields: '*',
        },
        releaseDate: {
          fields: '*',
        },
        gallery: {
          fields: '*',
        },
      },
    },
    { encodeValuesOnly: true }
  )

  return getRelease(id, query)
}

export const getServerSideProps: GetServerSideProps<ReleasePageProps> = async ({
  query,
}) => {
  const { slug } = query

  const slugTarget = slug?.at(-1)

  if (!slug || !slugTarget || !Array.isArray(slug)) return { notFound: true }

  try {
    const releaseId = Number(slugTarget.split('-').slice(-1)) || undefined

    if (!releaseId) return { notFound: true }

    const { data, error } = await fetchRelease(releaseId)

    if (!data || error) {
      console.error(error)
      return { notFound: true }
    }

    return {
      props: {
        isReleaseCalendarPage: true,
        release: flatCollectionType(data),
      },
    }
  } catch (err) {
    console.error(err)
    return { notFound: true }
  }
}
